/**
 * SupportChatWidget — floating bubble in the bottom-right of every
 * authenticated app screen. Lets the user write to the support team
 * without leaving their current context.
 *
 * Architecture
 *   • Closed state: 56×56 emerald bubble with an unread badge when
 *     admins have replied since the user last opened the widget.
 *   • Open state:   320×440 panel with a scrollable message log,
 *     reply textarea and send button. Polls every 15s for new admin
 *     messages so a fresh reply pops in without a manual refresh.
 *
 * Endpoints
 *   GET  /api/support/chat/messages   → user's own thread.
 *   POST /api/support/chat/send       → user → admin.
 *
 * Unread tracking is local-only: we keep `lumfi.chat.lastSeenAt` in
 * localStorage and count messages with direction=out + created_at >
 * lastSeenAt. Resets when the user opens the panel.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const STORAGE_KEY = "lumfi.chat.lastSeenAt";

function readLastSeen() {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function writeLastSeen(iso) {
  try { localStorage.setItem(STORAGE_KEY, iso); } catch { /* noop */ }
}

export function SupportChatWidget() {
  const { lang } = useLang();
  const { user } = useAuth();
  const isEs = lang === "es";
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [lastSeen, setLastSeen] = useState(() => readLastSeen());
  const qc = useQueryClient();

  // Only fetch when the user is authenticated — guests don't get a
  // support thread (and the endpoint would 401 anyway).
  const { data } = useQuery({
    queryKey: ["support-chat-my-messages"],
    queryFn: async () => (await api.get("/support/chat/messages")).data,
    enabled: !!user,
    refetchInterval: open ? 10_000 : 30_000,
    retry: false,
  });
  const messages = data?.messages || [];

  // Unread = admin replies (out) newer than lastSeen.
  const unread = useMemo(() => {
    if (!lastSeen) return messages.filter((m) => m.direction === "out").length;
    return messages.filter(
      (m) => m.direction === "out" && (m.created_at || "") > lastSeen,
    ).length;
  }, [messages, lastSeen]);

  // Bump lastSeen when the user opens the widget.
  useEffect(() => {
    if (open && messages.length > 0) {
      const latest = messages[messages.length - 1]?.created_at;
      if (latest) {
        writeLastSeen(latest);
        setLastSeen(latest);
      }
    }
  }, [open, messages]);

  const send = useMutation({
    mutationFn: (body) => api.post("/support/chat/send", { body }),
    onSuccess: (r) => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["support-chat-my-messages"] });
      // If the AI replied inline, surface a subtle toast so the user
      // knows the answer landed (helpful on web where the panel may be
      // collapsed by the time the reply arrives).
      if (r?.data?.ai_reply) {
        toast.success(isEs ? "LumiBot respondió" : "LumiBot replied");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "error"),
  });

  if (!user) return null;

  return (
    <div
      // On mobile (default) the chat bubble sits ABOVE the bottom
      // navigation (~64px tall) so it doesn't intercept taps on the
      // 'Más' button. On desktop (lg+) the bottom nav doesn't exist,
      // so we fall back to a flush 16px offset.
      className="fixed bottom-20 lg:bottom-4 right-4 z-50"
      data-testid="support-chat-widget"
    >
      {/* Bubble — closed state */}
      {!open && (
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={() => setOpen(true)}
          data-testid="support-chat-bubble"
          aria-label={isEs ? "Abrir soporte" : "Open support"}
          className="relative h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        >
          <MessageCircle className="h-6 w-6" />
          {unread > 0 && (
            <span
              data-testid="support-chat-bubble-unread"
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {/* Panel — open state */}
      {open && (
        <div
          data-testid="support-chat-panel"
          className="w-[calc(100vw-32px)] sm:w-96 h-[min(450px,calc(100vh-160px))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-emerald-600 text-white">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              <h3 className="font-semibold text-sm">
                {isEs ? "Soporte Lumfi" : "Lumfi support"}
              </h3>
            </div>
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              onClick={() => setOpen(false)}
              data-testid="support-chat-close"
              aria-label={isEs ? "Cerrar" : "Close"}
              className="text-white/80 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/30"
            data-testid="support-chat-messages"
          >
            {messages.length === 0 && (
              <div className="text-center text-xs text-slate-500 italic py-8">
                {isEs
                  ? "👋 Hola! ¿En qué te podemos ayudar hoy? Te respondemos en horario hábil." // design-system: ALLOW user-copy
                  : "👋 Hi! How can we help today? We reply during business hours."} // design-system: ALLOW user-copy
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.direction === "in" ? "justify-end" : "justify-start"}`}
                data-testid={`support-chat-msg-${m.id}`}
              >
                <div
                  className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm ${
                    m.direction === "in"
                      ? "bg-emerald-600 text-white rounded-br-sm"
                      : (m.ai_generated
                          ? "bg-sky-50 text-sky-900 border border-sky-200 rounded-bl-sm"
                          : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm")
                  }`}
                >
                  {m.direction === "out" && m.ai_generated && (
                    <div className="flex items-center gap-1 mb-1 text-[9px] font-bold uppercase tracking-wide text-sky-600">
                      {isEs ? "✨ LumiBot · IA" : "✨ LumiBot · AI"}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap leading-snug">{m.body}</p>
                  <p className={`text-[9px] mt-1 text-right ${m.direction === "in" ? "text-emerald-50/80" : "text-slate-400"}`}>
                    {new Date(m.created_at).toLocaleTimeString(isEs ? "es-CO" : "en-US",
                      { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <form
            className="flex gap-2 p-2 border-t border-slate-200 bg-white"
            onSubmit={(e) => {
              e.preventDefault();
              if (!draft.trim()) return;
              send.mutate(draft.trim());
            }}
          >
            <input /* design-system: ALLOW legacy-todo */
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={isEs ? "Escribe tu mensaje…" : "Type your message…"}
              data-testid="support-chat-input"
              className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
            />
            <button /* design-system: ALLOW legacy-todo */
              type="submit"
              disabled={send.isPending || !draft.trim()}
              data-testid="support-chat-send"
              className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
              aria-label={isEs ? "Enviar" : "Send"}
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
